import { useState, useEffect, useCallback } from 'react';
import { Mail, Check, X, Loader2 } from 'lucide-react';
import { getMyPendingInvites, respondToOrgInvite, type PendingInvite } from '@/db/organizations';
import { useOrganization } from '@/context/OrganizationContext';
import { useAuth } from '@/context/AuthContext';

export function PendingInvitesBanner() {
  const { user } = useAuth();
  const { refreshOrgs } = useOrganization();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState<string | null>(null);

  const loadInvites = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getMyPendingInvites();
      setInvites(data);
    } catch {
      // silent
    }
  }, [user]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const handleRespond = async (inviteId: string, accept: boolean) => {
    setLoading(inviteId);
    try {
      await respondToOrgInvite(inviteId, accept);
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      if (accept) {
        await refreshOrgs();
      }
    } catch {
      // silent
    } finally {
      setLoading(null);
    }
  };

  const roleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Proprietario';
      case 'admin': return 'Amministratore';
      default: return 'Membro';
    }
  };

  if (invites.length === 0) return null;

  return (
    <div className="px-4 py-2 space-y-2">
      {invites.map((invite) => (
        <div
          key={invite.id}
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-accent-violet/10 border border-accent-violet/20"
        >
          <div className="p-1.5 rounded-lg bg-accent-violet/20">
            <Mail className="w-4 h-4 text-accent-violet" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-medium text-text-primary truncate">
              Invito a <span className="font-semibold">{invite.org_name}</span>
            </p>
            <p className="text-[10px] text-text-tertiary">
              da {invite.invited_by_name} · {roleLabel(invite.role)}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handleRespond(invite.id, true)}
              disabled={loading === invite.id}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-accent-emerald text-white text-[11px] font-medium hover:bg-accent-emerald/90 disabled:opacity-50 transition-colors"
            >
              {loading === invite.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Accetta
            </button>
            <button
              onClick={() => handleRespond(invite.id, false)}
              disabled={loading === invite.id}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface-tertiary text-text-secondary text-[11px] font-medium hover:bg-surface-hover disabled:opacity-50 transition-colors"
            >
              <X className="w-3 h-3" />
              Rifiuta
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
